import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resolveAuthToken } from "../utils/resolveAuthToken";
import { useAuth } from "./AuthContext";
import type { SubApp } from "../data/appShellTypes";
import { detectSurface } from "../utils/surfaceDetection";

interface PartnerContext {
  partnerId: string;
  partnerSlug: string;
  partnerName: string;
  role: "admin" | "member";
}

interface AppShellContextValue {
  entitledApps: SubApp[];
  currentApp: SubApp | null;
  switchApp: (slug: string) => void;
  isLoading: boolean;
  currentPartner: PartnerContext | null;
  partners: PartnerContext[];
  switchPartner: (partnerId: string) => void;
}

const AppShellContext = createContext<AppShellContextValue | null>(null);

/** Map URL path prefix → app slug */
const PATH_TO_SLUG: Record<string, string> = {
  "/studio": "studio",
  "/docs": "docs",
  "/platform": "platform",
  "/enable": "enable",
};

function resolveAppFromPath(pathname: string): string | null {
  // Share-link visitors live under `/share/<token>/...` and shares are
  // always read-only docs surfaces, so resolve them straight to "docs".
  if (/^\/share\/[^/]+/.test(pathname)) return "docs";
  // Check longest prefix first
  const sorted = Object.keys(PATH_TO_SLUG).sort((a, b) => b.length - a.length);
  for (const prefix of sorted) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return PATH_TO_SLUG[prefix];
    }
  }
  // Also check versioned CMS routes (/studio/:project/:section/*)
  if (/^\/studio\/[^/]+\/[^/]+\/docs/.test(pathname) || /^\/studio\/[^/]+\/[^/]+\/api-reference/.test(pathname)) {
    return "studio";
  }
  return null;
}

const DOCS_APP_FALLBACK: SubApp = {
  slug: "docs",
  name: "Docs",
  description: "Documentation viewer",
  icon: "book-open",
  defaultPath: "/docs",
  audienceScope: ["internal", "external"],
  brandIdentifier: "cortex",
};

export function AppShellProvider({ children }: { children: ReactNode }) {
  const { accessToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [entitledApps, setEntitledApps] = useState<SubApp[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const lastVisited = useRef<Record<string, string>>({});
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);

  // Fetch entitled apps — start immediately if we have a token (don't wait for auth to fully resolve)
  useEffect(() => {
    const token = resolveAuthToken().token ?? accessToken;
    const onShareSurface = /^\/share\/[^/]+/.test(location.pathname);
    if (!token) {
      // Share-link visitors aren't logged in but still need the docs app
      // resolved so the section-tabs slot, project switcher, etc. render.
      setEntitledApps(onShareSurface ? [DOCS_APP_FALLBACK] : []);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/me/apps", {
              headers: { Authorization: `Bearer ${token}` },
            });
        if (!res.ok) { setEntitledApps([]); return; }
        const data: SubApp[] = await res.json();
        // Only expose Cortex, Docs, and Enable for now
        const ALLOWED_SLUGS = new Set(["studio", "docs", "enable"]);
        let filtered = data.filter(a => ALLOWED_SLUGS.has(a.slug));
        // On dedicated docs surfaces (docs.joveo.ai), only show docs — no studio
        const surface = detectSurface();
        if (surface.isDedicatedSurface) {
          filtered = filtered.filter(a => a.slug === surface.appSlug);
        }
        // The gated /api/me/apps response is authoritative — it already drops
        // Docs for partners whose admin disabled the Docs app. Don't re-inject
        // it here (that would override the per-partner app-access gate). Anonymous
        // share-link visitors still get a Docs fallback via DOCS_APP_FALLBACK above.
        if (!cancelled) setEntitledApps(filtered);
      } catch {
        if (!cancelled) setEntitledApps([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accessToken]);

  // Resolve current app from URL
  const currentSlug = resolveAppFromPath(location.pathname);
  const currentApp = entitledApps.find((a) => a.slug === currentSlug) ?? null;

  // Track last-visited URL per app
  useEffect(() => {
    if (currentSlug) {
      lastVisited.current[currentSlug] = location.pathname + location.search;
    }
  }, [location.pathname, location.search, currentSlug]);

  const switchApp = useCallback(
    (slug: string) => {
      const app = entitledApps.find((a) => a.slug === slug);
      if (!app) return;
      // For studio, always use defaultPath since URL structure changed
      // (old URLs like /cms/default/... are invalid now)
      if (slug === "studio") {
        navigate(app.defaultPath);
        return;
      }
      const target = lastVisited.current[slug] || app.defaultPath;
      navigate(target);
    },
    [entitledApps, navigate],
  );

  // Partner context resolution
  const partners: PartnerContext[] = useMemo(() => {
    if (!user?.partnerMemberships) return [];
    return user.partnerMemberships.map(pm => ({
      partnerId: pm.partnerId,
      partnerSlug: pm.partnerSlug,
      partnerName: pm.partnerName,
      role: pm.role,
    }));
  }, [user?.partnerMemberships]);

  // Resolve current partner: explicit selection > single partner > null
  const currentPartner = useMemo(() => {
    if (partners.length === 0) return null;
    if (selectedPartnerId) {
      return partners.find(p => p.partnerId === selectedPartnerId) || partners[0];
    }
    // Single partner → implicit
    if (partners.length === 1) return partners[0];
    // Multiple partners → default to first (user can switch)
    return partners[0];
  }, [partners, selectedPartnerId]);

  const switchPartner = useCallback((partnerId: string) => {
    setSelectedPartnerId(partnerId);
  }, []);

  return (
    <AppShellContext.Provider value={{
      entitledApps, currentApp, switchApp, isLoading,
      currentPartner, partners, switchPartner,
    }}>
      {children}
    </AppShellContext.Provider>
  );
}

export function useAppShell(): AppShellContextValue {
  const ctx = useContext(AppShellContext);
  if (!ctx) throw new Error("useAppShell must be used within <AppShellProvider>");
  return ctx;
}
